import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import { Intent, ProgressBar, Button, Icon, ProgressBarProps } from '@blueprintjs/core';
import { push as pushLocation } from 'connected-react-router';
import _ from 'lodash';
import { DatasetStatus } from '@retro-ui/api-contract';

import {
  StartingMaterialReducer,
  DatasetReducerDataItem,
} from '@f/services/starting-material/starting-material.types';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';

import cls from './useStartingMaterialDatasetUpload.module.scss';

type ToastMessageProps = {
  name: string;
  status: string;
  progress: number;
  onDismiss: () => void;
  onNavigateToDatasetList: () => void;
};

function getProgressBarProps(status: string, progress: number): ProgressBarProps {
  if (status === DatasetStatus.SENDING) {
    return {
      animate: true,
      stripes: true,
      intent: Intent.WARNING,
      value: progress,
    };
  }

  if (status === DatasetStatus.PROCESSING) {
    return {
      animate: true,
      stripes: true,
      intent: Intent.WARNING,
      value: 1,
    };
  }

  if (status === DatasetStatus.READY) {
    return {
      animate: false,
      stripes: false,
      intent: Intent.SUCCESS,
      value: 1,
    };
  }
  if (status === DatasetStatus.ERROR) {
    return {
      animate: false,
      stripes: false,
      intent: Intent.DANGER,
      value: 1,
    };
  }

  return {
    animate: true,
    stripes: true,
    value: 1,
  };
}

const beforeWindowUnload = (event: BeforeUnloadEvent): BeforeUnloadEvent => {
  event.preventDefault();
  event.returnValue = 'Changes you made may not be saved.';
  return event;
};

function dismissToast(toastId: string): void {
  AppToaster.dismiss(toastId);
}

function buildToastMessage(props: ToastMessageProps) {
  const { name, status, progress, onNavigateToDatasetList, onDismiss } = props;
  const progressBarProps = getProgressBarProps(status, progress);

  return (
    <div className={cls.uploadingToastContainer}>
      <div className={cls.uploadingLabel}>
        {status === DatasetStatus.SENDING && (
          <>
            <div>
              <Icon className={cls.uploadingIcon} icon="cloud-upload" />
              <span>
                Uploading dataset <strong>{name}</strong>
              </span>
            </div>
            <span>{Number(progress * 100).toFixed(1)}%</span>
          </>
        )}

        {status === DatasetStatus.PROCESSING && (
          <div>
            <Icon className={cls.uploadingIcon} icon="cloud" />
            <span>
              Dataset <strong>{name}</strong> is being processed on the server
            </span>
          </div>
        )}

        {status === DatasetStatus.READY && (
          <>
            <div>
              <Icon className={cls.uploadingIcon} icon="tick" />
              <span>
                Dataset <strong>{name}</strong> is available
              </span>
            </div>
            <Button className={cls.dismissButton} small minimal icon="cross" onClick={onDismiss} />
          </>
        )}

        {status === DatasetStatus.ERROR && (
          <>
            <div>
              <Icon className={cls.uploadingIcon} icon="error" />
              <span>
                Failed to process dataset <strong>{name}</strong>
              </span>
            </div>
            <Button
              className={cls.dismissButton}
              small
              minimal
              text="See more"
              onClick={onNavigateToDatasetList}
            />
            <Button className={cls.dismissButton} small minimal icon="cross" onClick={onDismiss} />
          </>
        )}
      </div>
      <ProgressBar {...progressBarProps} className={cls.uploadProgressBar} />
    </div>
  );
}

export function useStartingMaterialDatasetUpload(): void {
  const dispatch = useDispatch();
  const startingMaterial = useSelector(
    (state: any) => state.startingMaterial as StartingMaterialReducer
  );
  const { data } = startingMaterial.dataset;

  const [activeUploadIds, setActiveUploadIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    setActiveUploadIds(
      _.chain(data)
        .values()
        .filter(['isPrivate', true])
        .filter(item => [DatasetStatus.SENDING, DatasetStatus.PROCESSING].includes(item.status))
        .map('id')
        .thru(arr => [...activeUploadIds, ...arr]) // In order to store previously added ids whih may turn to be AVAILABLE
        .uniq()
        .value()
    );

    const activeUploads = _.chain(data).pick(activeUploadIds).values().value();
    const uploadsSending = _.filter(activeUploads, ['status', DatasetStatus.SENDING]);

    if (uploadsSending.length > 0) {
      window.addEventListener('beforeunload', beforeWindowUnload, { capture: true });
    } else {
      window.removeEventListener('beforeunload', beforeWindowUnload, { capture: true });
    }

    const notAvailableDatasetsIds = _.chain(data)
      .values()
      .filter(item => [DatasetStatus.REMOVED, DatasetStatus.CANCELED].includes(item.status))
      .map('id')
      .value();

    activeUploads.forEach((item: DatasetReducerDataItem) => {
      const { id, name, status, uploadedSize, size } = item;

      const progress = uploadedSize / size;

      const onDismiss = () => {
        dismissToast(id);
      };

      AppToaster.show(
        {
          className: cx(cls.uploadingToast, { [cls.uploadCompleted]: progress === 1 }),
          message: buildToastMessage({
            name,
            status,
            progress,
            onNavigateToDatasetList: () => {
              dispatch(pushLocation(`/dashboard/starting-materials?openDataset=${id}`));
            },
            onDismiss,
          }),
          timeout: 0,
        },
        id
      );
    });

    notAvailableDatasetsIds.forEach(id => {
      dismissToast(id);
    });
  }, [JSON.stringify(data)]);
}
